// BoardMembersPage.js
import React, { useState, useEffect } from "react";
import { db, auth, storage } from "./firebase.js";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

function BoardMembersPage({ user, isAdmin, isMobile }) {
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [photo, setPhoto] = useState(null);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editPhoto, setEditPhoto] = useState(null);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        console.log("Fetching board members from Firestore...");
        const snapshot = await getDocs(collection(db, "board_members"));
        console.log("Board members snapshot size:", snapshot.size);
        const memberList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log("Board members list:", memberList);
        setMembers(memberList);
      } catch (error) {
        console.error("Error fetching board members:", error);
        setError("Failed to fetch board members: " + error.message);
      }
    };
    fetchMembers();
  }, []);

  console.log("BoardMembersPage rendering with isAdmin:", isAdmin);

  const addMember = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No authenticated user");
      console.log("Forcing token refresh before write...");
      const token = await currentUser.getIdToken(true);
      const idTokenResult = await currentUser.getIdTokenResult();
      console.log("Refreshed token:", token);
      console.log("Token claims during write:", idTokenResult.claims);

      let photoUrl = "";
      if (photo) {
        const photoRef = ref(storage, `BoardMemberPhotos/${Date.now()}_${photo.name}`);
        await uploadBytes(photoRef, photo);
        photoUrl = await getDownloadURL(photoRef);
        console.log("Photo uploaded, URL:", photoUrl);
      }

      await addDoc(collection(db, "board_members"), {
        name,
        position,
        contact,
        email,
        photoUrl,
        createdBy: currentUser.uid,
        createdAt: new Date(),
      });
      console.log("Board member added successfully");
      setName("");
      setPosition("");
      setContact("");
      setEmail("");
      setPhoto(null);
      const snapshot = await getDocs(collection(db, "board_members"));
      const memberList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMembers(memberList);
    } catch (error) {
      console.error("Error adding board member:", error);
      setError("Failed to add board member: " + error.message);
    }
  };

  const editMemberData = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No authenticated user");
      console.log("Forcing token refresh before update...");
      const token = await currentUser.getIdToken(true);
      const idTokenResult = await currentUser.getIdTokenResult();
      console.log("Refreshed token:", token);
      console.log("Token claims during update:", idTokenResult.claims);

      let photoUrl = editMember.photoUrl || "";
      if (editPhoto) {
        const photoRef = ref(storage, `BoardMemberPhotos/${Date.now()}_${editPhoto.name}`);
        await uploadBytes(photoRef, editPhoto);
        photoUrl = await getDownloadURL(photoRef);
        console.log("Photo updated, URL:", photoUrl);
      }

      const memberRef = doc(db, "board_members", editMember.id);
      await updateDoc(memberRef, {
        name: editMember.name,
        position: editMember.position,
        contact: editMember.contact,
        email: editMember.email,
        photoUrl,
        updatedAt: new Date(),
      });
      console.log("Board member updated successfully");
      setEditModalOpen(false);
      setEditMember(null);
      setEditPhoto(null);
      const snapshot = await getDocs(collection(db, "board_members"));
      const memberList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMembers(memberList);
    } catch (error) {
      console.error("Error updating board member:", error);
      setError("Failed to update board member: " + error.message);
    }
  };

  const deleteMember = async (id) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No authenticated user");
      console.log("Forcing token refresh before delete...");
      const token = await currentUser.getIdToken(true);
      const idTokenResult = await currentUser.getIdTokenResult();
      console.log("Refreshed token:", token);
      console.log("Token claims during delete:", idTokenResult.claims);
      await deleteDoc(doc(db, "board_members", id));
      console.log("Board member deleted successfully");
      setMembers(members.filter(member => member.id !== id));
    } catch (error) {
      console.error("Error deleting board member:", error);
      setError("Failed to delete board member: " + error.message);
    }
  };

  const openEditModal = (member) => {
    setEditMember({ ...member });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditMember(null);
    setEditPhoto(null);
    setError(null);
  };

  return (
    <div className="page-container">
      <style>
        {`
          .page-container {
            padding: 2rem;
          }
          .page-title {
            font-size: 1.5rem;
            margin-bottom: 1rem;
          }
          .error-message {
            color: #ef4444;
            margin-bottom: 1rem;
          }
          .add-form {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-bottom: 1rem;
          }
          .form-input, .form-file {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            border-radius: 0.25rem;
            flex: 1;
            min-width: 150px;
            font-size: 1rem;
          }
          .add-button {
            background: #3b82f6;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .add-button:hover {
            background: #2563eb;
          }
          .table-container {
            overflow-x: auto;
            position: relative;
            z-index: 1;
          }
          .member-table {
            min-width: 100%;
            border-collapse: collapse;
          }
          .table-header {
            background: #1a3c34;
            color: white;
          }
          .table-header-cell {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            text-align: left;
            white-space: nowrap;
          }
          .table-row:nth-child(even) {
            background: #f3f4f6;
          }
          .table-row:nth-child(odd) {
            background: #e5e7eb;
          }
          .table-cell {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            white-space: pre-line;
          }
          .photo-cell {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .member-photo {
            width: 100px;
            height: 100px;
            object-fit: cover;
            border-radius: 0.25rem;
          }
          .action-buttons {
            display: flex;
            gap: 0.5rem;
          }
          .edit-button {
            background: #eab308;
            color: white;
            padding: 0.25rem 0.75rem;
            border-radius: 0.25rem;
          }
          .edit-button:hover {
            background: #ca8a04;
          }
          .delete-button {
            background: #ef4444;
            color: white;
            padding: 0.25rem 0.75rem;
            border-radius: 0.25rem;
          }
          .delete-button:hover {
            background: #dc2626;
          }
          .no-members {
            color: #4b5563;
          }
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: white;
            padding: 1.5rem;
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            max-width: 500px;
            width: 90%;
            z-index: 1001;
          }
          .modal-title {
            font-size: 1.25rem;
            margin-bottom: 1rem;
          }
          .modal-form {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          .form-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          .form-label {
            color: #374151;
          }
          .form-input-modal, .form-file-modal {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            border-radius: 0.25rem;
            width: 100%;
            font-size: 1rem;
          }
          .modal-buttons {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
          }
          .cancel-button {
            background: #6b7280;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .cancel-button:hover {
            background: #4b5563;
          }
          .save-button {
            background: #3b82f6;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .save-button:hover {
            background: #2563eb;
          }
          @media (max-width: 640px) {
            .page-container {
              padding: 1rem;
            }
            .page-title {
              font-size: 1.25rem;
            }
            .error-message {
              font-size: 0.875rem;
            }
            .add-form {
              flex-direction: column;
              gap: 0.5rem;
            }
            .form-input, .form-file {
              min-width: 100%;
              font-size: 0.9rem;
              padding: 0.4rem;
            }
            .add-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
            .table-header-cell,
            .table-cell,
            .photo-cell {
              padding: 0.4rem;
              font-size: 0.875rem;
            }
            .member-photo {
              width: 80px;
              height: 80px;
            }
            .action-buttons {
              flex-direction: column;
              gap: 0.25rem;
            }
            .edit-button,
            .delete-button {
              padding: 0.25rem 0.5rem;
              font-size: 0.875rem;
            }
            .no-members {
              font-size: 0.875rem;
            }
            .modal-content {
              width: 95%;
              padding: 1rem;
              max-width: 350px;
            }
            .modal-title {
              font-size: 1rem;
            }
            .form-input-modal, .form-file-modal {
              font-size: 0.9rem;
              padding: 0.4rem;
            }
            .form-label {
              font-size: 0.875rem;
            }
            .cancel-button,
            .save-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
          }
        `}
      </style>
      <h2 className="page-title">Board Members</h2>
      {error && <p className="error-message">{error}</p>}
      {isAdmin && (
        <form onSubmit={addMember} className="add-form">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setPhoto(e.target.files[0])}
            className="form-file"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className="form-input"
            required
          />
          <input
            type="text"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder="Position"
            className="form-input"
            required
          />
          <input
            type="text"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="Contact Info"
            className="form-input"
            required
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email Address"
            className="form-input"
            required
          />
          <button type="submit" className="add-button">
            Add Board Member
          </button>
        </form>
      )}
      <div className="table-container">
        {members.length > 0 ? (
          <table className="member-table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Photo</th>
                <th className="table-header-cell">Name</th>
                <th className="table-header-cell">Position</th>
                <th className="table-header-cell">Contact</th>
                <th className="table-header-cell">Email</th>
                {isAdmin && (
                  <th className="table-header-cell">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {members.map(member => (
                <tr key={member.id} className="table-row">
                  <td className="photo-cell">
                    <img
                      src={member.photoUrl || "https://via.placeholder.com/100"}
                      alt={member.name}
                      className="member-photo"
                    />
                  </td>
                  <td className="table-cell">{member.name}</td>
                  <td className="table-cell">{member.position}</td>
                  <td className="table-cell">{member.contact}</td>
                  <td className="table-cell">{member.email || "N/A"}</td>
                  {isAdmin && (
                    <td className="table-cell">
                      <div className="action-buttons">
                        <button
                          onClick={() => openEditModal(member)}
                          className="edit-button"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteMember(member.id)}
                          className="delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="no-members">No board members found</p>
        )}
      </div>

      {editModalOpen && (
        <div className="modal-overlay">
          <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
            <h3 className="modal-title">Edit Board Member</h3>
            {error && <p className="error-message">{error}</p>}
            <form onSubmit={editMemberData} className="modal-form">
              <div className="form-group">
                <label className="form-label">Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditPhoto(e.target.files[0])}
                  className="form-file-modal"
                />
                {editMember?.photoUrl && (
                  <img
                    src={editMember.photoUrl}
                    alt="Current"
                    className="member-photo"
                    style={{ marginTop: '0.5rem' }}
                  />
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={editMember?.name || ""}
                  onChange={(e) => setEditMember({ ...editMember, name: e.target.value })}
                  className="form-input-modal"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Position</label>
                <input
                  type="text"
                  value={editMember?.position || ""}
                  onChange={(e) => setEditMember({ ...editMember, position: e.target.value })}
                  className="form-input-modal"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Contact</label>
                <input
                  type="text"
                  value={editMember?.contact || ""}
                  onChange={(e) => setEditMember({ ...editMember, contact: e.target.value })}
                  className="form-input-modal"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={editMember?.email || ""}
                  onChange={(e) => setEditMember({ ...editMember, email: e.target.value })}
                  className="form-input-modal"
                  required
                />
              </div>
              <div className="modal-buttons">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BoardMembersPage;